import { useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import type { ProblemRecord, ProblemStage } from "@/lib/problem-record-data";
import {
  STAGE_REQUIREMENTS,
  activateStage,
  approveStage,
  completeStage,
  saveStageDraft,
  validateStageCompletion,
} from "@/lib/problem-record-workflow";
import { saveProblemRecord } from "@/lib/problem-record-store";

const APPROVERS = [
  "D. Shumar — VP Quality",
  "P. Andersson — Director Engineering",
  "M. Chen — Quality Engineering",
  "T. Yamada — Validation Engineering",
];

export function StageWorkspaceDialog({
  record,
  stageKey,
  onClose,
}: {
  record: ProblemRecord;
  stageKey: string;
  onClose: () => void;
}) {
  const stage = record.stages.find((s) => s.key === stageKey) as ProblemStage;
  const req = STAGE_REQUIREMENTS[stageKey];
  const actor = record.owner;

  const [evidence, setEvidence] = useState<Record<string, string>>({ ...(stage.evidence ?? {}) });
  const [owner, setOwner] = useState(stage.owner ?? "");
  const [due, setDue] = useState(stage.due ?? "");
  const [note, setNote] = useState(stage.completionNote ?? "");
  const [approver, setApprover] = useState(stage.approver ?? APPROVERS[0]);
  const [errors, setErrors] = useState<string[]>([]);

  const working = useMemo(
    () =>
      saveStageDraft(record, stageKey, { owner, due, evidence, completionNote: note, approver }),
    [record, stageKey, owner, due, evidence, note, approver],
  );
  const liveErrors = validateStageCompletion(working, stageKey);
  const approved = working.stages.find((s) => s.key === stageKey)?.approvalStatus === "Approved";

  function persist(next: ProblemRecord, message: string, description?: string) {
    saveProblemRecord(next);
    toast.success(message, description ? { description } : undefined);
  }

  function handleSave() {
    persist(working, "Stage evidence saved", `${stage.label} — draft evidence recorded`);
    onClose();
  }

  function handleActivate() {
    persist(
      activateStage(working, stageKey, actor),
      `${stage.label} activated`,
      "Audit entry added",
    );
    onClose();
  }

  function handleApprove() {
    persist(approveStage(working, stageKey, approver), `${stage.label} approved`, approver);
  }

  function handleComplete() {
    const result = completeStage(working, stageKey, actor);
    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Stage cannot be completed", {
        description: `${result.errors.length} requirement(s) outstanding`,
      });
      return;
    }
    let next = result.record;
    if (result.nextStageKey) {
      const nextLabel = next.stages.find((s) => s.key === result.nextStageKey)!.label;
      if (window.confirm(`${stage.label} completed. Activate the next stage — ${nextLabel}?`)) {
        next = activateStage(next, result.nextStageKey, actor);
      }
    }
    persist(
      next,
      `${stage.label} completed`,
      stageKey === "prevention" ? "Record closed" : undefined,
    );
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-background/70 backdrop-blur-sm">
      <button className="flex-1" aria-label="Close stage workspace" onClick={onClose} />
      <div className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-border bg-card">
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-border bg-card px-6 py-5">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Stage {stage.order} of 11 · {record.id}
            </div>
            <h2 className="mt-1 text-lg font-semibold">{stage.label}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{req?.intent}</p>
          </div>
          <span
            className={`ml-auto shrink-0 rounded border px-2 py-0.5 text-[11px] font-medium ${
              stage.state === "Complete"
                ? "border-success/40 bg-success/10 text-success"
                : stage.state === "Active"
                  ? "border-primary/50 bg-primary/15 text-primary"
                  : "border-border bg-muted text-muted-foreground"
            }`}
          >
            {stage.state}
          </span>
          <button
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Stage owner">
              <input
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </Field>
            <Field label="Due date">
              <input
                value={due}
                onChange={(e) => setDue(e.target.value)}
                placeholder="YYYY-MM-DD"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </Field>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Required evidence
            </div>
            <div className="space-y-3">
              {req?.fields.map((f) => {
                const value = evidence[f.key] ?? "";
                const set = (v: string) => setEvidence((p) => ({ ...p, [f.key]: v }));
                return (
                  <Field key={f.key} label={`${f.label}${f.optional ? " (conditional)" : ""}`}>
                    {f.type === "textarea" ? (
                      <textarea
                        value={value}
                        rows={3}
                        onChange={(e) => set(e.target.value)}
                        className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                      />
                    ) : f.type === "yesno" ? (
                      <div className="flex gap-2">
                        {["Yes", "No"].map((o) => (
                          <button
                            key={o}
                            onClick={() => set(value === o ? "" : o)}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                              value === o
                                ? "border-primary bg-primary/15 text-primary"
                                : "border-border text-muted-foreground hover:bg-muted"
                            }`}
                          >
                            {o}
                          </button>
                        ))}
                      </div>
                    ) : f.type === "select" ? (
                      <select
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                      >
                        {(f.options ?? []).map((o) => (
                          <option key={o} value={o}>
                            {o || "— select —"}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={value}
                        placeholder={f.placeholder}
                        onChange={(e) => set(e.target.value)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                      />
                    )}
                  </Field>
                );
              })}
            </div>
          </div>

          <Field label="Completion note / evidence summary">
            <textarea
              value={note}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
              className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </Field>

          {req?.approvalRequired && (
            <div className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ShieldCheck className="h-4 w-4 text-primary" /> Approval required —{" "}
                {req.approvalRole}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  value={approver}
                  onChange={(e) => setApprover(e.target.value)}
                  className="rounded-md border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary"
                >
                  {APPROVERS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
                {approved ? (
                  <span className="inline-flex items-center gap-1 rounded border border-success/40 bg-success/10 px-2 py-1 text-xs font-medium text-success">
                    <Check className="h-3 w-3" /> Approved ·{" "}
                    {working.stages.find((s) => s.key === stageKey)?.approver}
                  </span>
                ) : (
                  <button
                    onClick={handleApprove}
                    className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/15"
                  >
                    Record approval
                  </button>
                )}
              </div>
            </div>
          )}

          {(errors.length > 0 || liveErrors.length > 0) && stage.state !== "Complete" && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
              <div className="mb-1 inline-flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" /> Outstanding before this stage can complete
              </div>
              <ul className="list-inside list-disc space-y-0.5">
                {(errors.length ? errors : liveErrors).map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 mt-auto flex flex-wrap items-center gap-2 border-t border-border bg-card px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
          >
            Save evidence
          </button>
          {stage.state === "Pending" && (
            <button
              onClick={handleActivate}
              className="ml-auto rounded-md border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/15"
            >
              Activate stage
            </button>
          )}
          {stage.state !== "Complete" && (
            <button
              onClick={handleComplete}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${
                stage.state === "Pending" ? "" : "ml-auto"
              } bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              <CheckCircle2 className="h-4 w-4" /> Complete stage
            </button>
          )}
          {stage.state === "Complete" && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-success">
              <Check className="h-4 w-4" /> Completed {stage.completedOn ?? ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
